<template>
	<div class="absolute -top-28 right-0 h-40 w-40">
		<div
			class="absolute top-24.5 right-16.5"
			:class="{
				'animate-fly-in-1': flyIn,
				'animate-zoom-in': bubblesAnimationZoom[0]
			}"
			v-if="bubbles[0]"
		>
			<div
				class="border-separator-secondary animate-hover-1 rounded-full border-2 p-0.5"
				v-if="!pop[0]"
			>
				<div
					class="bg-fill-secondary flex h-11 w-11 items-center justify-center rounded-full"
				>
					<LazySticker
						:src="bubbles[0]"
						class="h-3/5 w-3/5"
						autoplay
						:preloader="bubblesPreloaders[0]"
					/>
				</div>
			</div>
			<BubblePop
				class="text-separator-secondary absolute -top-3 -right-2 h-17 w-17"
				v-if="pop[0]"
			/>
		</div>
		<div
			class="absolute top-27 right-0.5"
			:class="{
				'animate-fly-in-2': flyIn,
				'animate-zoom-in': bubblesAnimationZoom[1]
			}"
			v-if="bubbles[1]"
		>
			<div
				class="border-separator-secondary animate-hover-2 rounded-full border-2 p-0.5"
				v-if="!pop[1]"
			>
				<div
					class="bg-fill-secondary flex h-8 w-8 items-center justify-center rounded-full"
				>
					<LazySticker
						:src="bubbles[1]"
						class="h-3/5 w-3/5"
						autoplay
						:preloader="bubblesPreloaders[1]"
					/>
				</div>
			</div>
			<BubblePop
				class="text-separator-secondary absolute -top-2 -right-1.5 h-13 w-13"
				v-if="pop[1]"
			/>
		</div>
		<div
			class="absolute top-12 -right-7"
			:class="{
				'animate-fly-in-3': flyIn,
				'animate-zoom-in': bubblesAnimationZoom[2]
			}"
			v-if="bubbles[2]"
		>
			<div
				class="border-separator-secondary animate-hover-3 rounded-full border-2 p-0.5"
				v-if="!pop[2]"
			>
				<div
					class="bg-fill-secondary flex h-8 w-8 items-center justify-center rounded-full"
				>
					<LazySticker
						:src="bubbles[2]"
						class="h-3/5 w-3/5"
						autoplay
						:preloader="bubblesPreloaders[2]"
					/>
				</div>
			</div>
			<BubblePop
				class="text-separator-secondary absolute -top-2 -right-1.5 h-13 w-13"
				v-if="pop[2]"
			/>
		</div>
		<div
			class="absolute top-14.5 right-7"
			:class="{
				'animate-fly-in-4': flyIn,
				'animate-zoom-in': bubblesAnimationZoom[3]
			}"
			v-if="bubbles[3]"
		>
			<div
				class="border-separator-secondary animate-hover-4 rounded-full border-2 p-0.5"
				v-if="!pop[3]"
			>
				<div
					class="bg-fill-secondary flex h-8 w-8 items-center justify-center rounded-full"
				>
					<LazySticker
						:src="bubbles[3]"
						class="h-3/5 w-3/5"
						autoplay
						:preloader="bubblesPreloaders[3]"
					/>
				</div>
			</div>
			<BubblePop
				class="text-separator-secondary absolute -top-2 -right-1.5 h-13 w-13"
				v-if="pop[3]"
			/>
		</div>
		<div
			class="absolute top-13 right-26"
			:class="{
				'animate-fly-in-5': flyIn,
				'animate-zoom-in': bubblesAnimationZoom[4]
			}"
			v-if="bubbles[4]"
		>
			<div
				class="border-separator-secondary animate-hover-5 rounded-full border-2 p-0.5"
				v-if="!pop[4]"
			>
				<div
					class="bg-fill-secondary flex h-8 w-8 items-center justify-center rounded-full"
				>
					<LazySticker
						:src="bubbles[4]"
						class="h-3/5 w-3/5"
						autoplay
						:preloader="bubblesPreloaders[4]"
					/>
				</div>
			</div>
			<BubblePop
				class="text-separator-secondary absolute -top-2 -right-1.5 h-13 w-13"
				v-if="pop[4]"
			/>
		</div>
		<div
			class="animate-live absolute right-4 bottom-5 flex items-center gap-1.5 bg-linear-to-l from-black/0 via-black/90 to-black/0 px-5 py-1"
			v-if="showLive"
		>
			<div class="bg-red h-2.25 w-2.25 animate-pulse rounded-full"></div>
			<div class="text-red text-sm font-medium uppercase">live</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import LazySticker from './LazySticker.vue'
import BubblePop from '@/assets/bubble-pop.svg'
import { sleep } from '@/utils/sleep'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useLive } from '@/stores/live'
import { animation } from '@/utils/urls'

// !!!
// important to know that replacing bubbles should only be done after the flyIn is false
// !!!

const flyIn = ref(true)

const pop = ref([false, false, false, false, false])

const bubbles = ref<string[]>(['', '', '', '', ''])
const bubblesPreloaders = ref<string[]>(['', '', '', '', ''])
const bubblesAnimationZoom = ref([false, false, false, false, false])

const showLive = computed(() => bubbles.value.some((bubble) => bubble !== ''))

const popAndReplace = async (
	index: number,
	newBubble: string,
	newPreloader?: string
) => {
	if (flyIn.value) {
		return
	}

	pop.value[index] = true

	await sleep(550)

	bubbles.value[index] = ''
	bubblesAnimationZoom.value[index] = true
	pop.value[index] = false

	await sleep(100)

	bubbles.value[index] = newBubble
	bubblesPreloaders.value[index] = newPreloader || ''
}

const live = useLive()
const interval = ref()
const lastTime = ref(0)
const lastIndex = ref(0)

const startInterval = () => {
	interval.value = setInterval(() => {
		const item = live.last.reverse().find((item) => item.time > lastTime.value)

		if (!item) {
			return
		}

		lastTime.value = item.time

		popAndReplace(
			lastIndex.value,
			animation(item.modelUuid),
			item.preloader || undefined
		)

		lastIndex.value = (lastIndex.value + 1) % bubbles.value.length
	}, 150)
}

const stopInterval = () => {
	if (interval.value) {
		clearInterval(interval.value)
		interval.value = undefined
	}
}

watch(
	live.last,
	async () => {
		if (
			!bubbles.value.every((bubble) => bubble === '') ||
			live.last.length !== 5
		) {
			return
		}

		for (let i = 0; i < live.last.length; i++) {
			const item = live.last[i]

			// @ts-ignore
			bubbles.value[i] = animation(item.modelUuid)
			bubblesPreloaders.value[i] = item.preloader || ''
		}

		const [item] = live.last

		if (item) {
			lastTime.value = item.time
		}

		await sleep(1300)
		flyIn.value = false
		startInterval()
	},
	{ immediate: true }
)

onUnmounted(() => stopInterval())
</script>
